import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectCreditCardsFullPaginatedData,
  selectErrorDetailMapFromState,
  selectFilteredCreditCardOptions,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectFilteredUserNonGuestEmailOptions,
  selectPostingCreditCardFromState,
  selectTopLevelErrorFromState,
  selectUsersFullPaginatedData,
} from 'src/app/CreateBudget/redux/createBudget.selectors'
import BudgetPickContent from 'src/components/BudgetPickContent/BudgetPickContent'
import BudgetRecurringContent from 'src/components/BudgetRecurringContent/BudgetRecurringContent'
import BudgetStandardContent from 'src/components/BudgetStandardContent/BudgetStandardContent'
import { CreditCardDropdownInfiniteScrollProps } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useFlyoutDispatch } from 'src/hooks'
import { CreditCardStatus, CreditCardType } from 'src/models/creditCard.models'
import { CreditCardPermissions } from 'src/models/creditCardPermissions.models'
import { useCreateCreditCard } from 'src/queries/creditCards/creditCardWrites.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  getCreditCard,
  getCreditCards,
} from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'

export enum BudgetScreen {
  Pick,
  Standard,
  Recurring,
}

const CreateBudget: React.FC = () => {
  const postingCreditCard = useSelector(selectPostingCreditCardFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const queryParams = useSelector(selectQueryParams)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const nonGuestUserEmailOptions = useSelector(
    selectFilteredUserNonGuestEmailOptions,
  )
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCardOptions = useSelector(selectFilteredCreditCardOptions)
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
    ] as const,
  })
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const user = useCurrentUser()
  const appName = useSelector(selectAppName)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const dispatch = useFlyoutDispatch()

  const createCreditCard = useCreateCreditCard()

  const dispatchPostCreditCardAndOpenToastAndCloseFlyout = (
    body: Partial<PostCreditCardRequest>,
  ): void => {
    createCreditCard.mutate(body)
  }
  const [screen, setScreen] = useState(BudgetScreen.Pick)

  useEffect((): void => {
    if (typeof queryParams['creditCardId'] === 'string') {
      dispatch(getCreditCard(queryParams['creditCardId']))
    }
  }, [queryParams['creditCardId']])

  const initialFlatFields = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const fetchCreditCards = (filterInput?: string, page?: number): void => {
    dispatch(
      getCreditCards({
        search: filterInput,
        count: 10,
        page: page,
        status: CreditCardStatus.Active,
        sortField: 'type',
        sortDirection: 'ASC',
        type: CreditCardType.Source,
        withPermission: CreditCardPermissions.IssueDelegateCreditCards,
        budgetsEnabled: true,
      }),
    )
  }

  // Pre-fetch credit cards so we can auto-select if only one is available
  useEffect((): void => {
    fetchCreditCards()
  }, [])

  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: fetchCreditCards,
    ...creditCardsFullPaginatedData,
  }

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {!statisticsResults[
        StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
      ].isSuccess ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <Helmet>
            <title>{`Create a Budget | ${appName}`}</title>
            <meta
              name="description"
              content={`Create a Budget page of ${appName}`}
            />
          </Helmet>
          {screen === BudgetScreen.Pick && (
            <BudgetPickContent setScreen={setScreen} />
          )}
          {screen === BudgetScreen.Standard && (
            <BudgetStandardContent
              postCreditCardOrBudgetRequest={
                dispatchPostCreditCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={
                showGuestRole
                  ? nonGuestUserEmailOptions
                  : filteredUserEmailOptions
              }
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
            />
          )}
          {screen === BudgetScreen.Recurring && (
            <BudgetRecurringContent
              postCreditCard={dispatchPostCreditCardAndOpenToastAndCloseFlyout}
              setScreen={setScreen}
              postingCreditCard={postingCreditCard}
              initialFlatFields={initialFlatFields}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              userOptions={
                showGuestRole
                  ? nonGuestUserEmailOptions
                  : filteredUserEmailOptions
              }
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              rcnOptions={filteredCreditCardOptions}
              creditCardInfiniteScrollProps={creditCardInfiniteScrollProps}
              isFormDisabled={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
            />
          )}
        </>
      )}
    </RightFlyout>
  )
}

export default CreateBudget
